import React, { useRef } from "react"
import PropTypes from "prop-types"
import { Box, Grid, Paper, Stack, Switch, Typography } from "@mui/material"
import DatePickerSelectV2 from "src/v2/components/Date/date-picker-select"
import TabsWrappedLabel from "src/v2/components/tabs/tabs-component"
import UploadComponent from "src/v2/components/upload/upload-component"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as PlayIcon } from "src/v2/assets/icons/play-icon.svg"
import { WORKFLOW_OPTIONS, WORKFLOW_TABS } from "../constants"

const WorkflowFileInput = ({
    label,
    fileInputRef,
    onFileSelect,
    resetKey,
}) => (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
        <Typography variant="contraHeading" color="primary.white70">
            {label}
        </Typography>
        <UploadComponent
            key={resetKey}
            compact
            accept=".xlsx"
            buttonLabel="SELECIONAR"
            helperText="Arraste o XLSX aqui"
            fileInputRef={fileInputRef}
            onFileSelect={onFileSelect}
        />
    </Stack>
)

WorkflowFileInput.propTypes = {
    label: PropTypes.string.isRequired,
    fileInputRef: PropTypes.object.isRequired,
    onFileSelect: PropTypes.func.isRequired,
    resetKey: PropTypes.string.isRequired,
}

const WorkflowForm = ({
    workflowType,
    dataInicial,
    dataFinal,
    useGpu,
    loading,
    resetKey,
    onWorkflowChange,
    onDataInicialChange,
    onDataFinalChange,
    onVerticesFileChange,
    onResumoFileChange,
    onUseGpuChange,
    onSubmit,
}) => {
    const verticesInputRef = useRef(null)
    const resumoInputRef = useRef(null)
    const currentWorkflow = WORKFLOW_OPTIONS[workflowType]

    return (
        <Paper sx={{ p: 3, backgroundColor: "background.paper" }}>
            <Stack spacing={3}>
                <TabsWrappedLabel
                    tabs={WORKFLOW_TABS}
                    value={workflowType}
                    onChange={onWorkflowChange}
                    noReload
                />

                <Grid container spacing={2} alignItems="stretch">
                    <Grid item xs={12} md={1.8} xl={1.8}>
                        <DatePickerSelectV2
                            value={dataInicial}
                            label="Data inicial"
                            onChange={onDataInicialChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={1.8} xl={1.8}>
                        <DatePickerSelectV2
                            value={dataFinal}
                            label="Data final"
                            onChange={onDataFinalChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={workflowType === "if" ? 2.45 : 3.1} xl={workflowType === "if" ? 2.6 : 3}>
                        <WorkflowFileInput
                            label={currentWorkflow.verticesLabel}
                            fileInputRef={verticesInputRef}
                            onFileSelect={onVerticesFileChange}
                            resetKey={`${resetKey}-${workflowType}-vertices`}
                        />
                    </Grid>

                    <Grid item xs={12} md={workflowType === "if" ? 2.45 : 3.1} xl={workflowType === "if" ? 2.6 : 3}>
                        <WorkflowFileInput
                            label={currentWorkflow.resumoLabel}
                            fileInputRef={resumoInputRef}
                            onFileSelect={onResumoFileChange}
                            resetKey={`${resetKey}-${workflowType}-resumo`}
                        />
                    </Grid>

                    {workflowType === "if" && (
                        <Grid item xs={12} md={1} xl={0.9}>
                            <Stack
                                spacing={1}
                                sx={{ height: "100%", justifyContent: "center" }}
                            >
                                <Typography variant="contraHeading" color="primary.white70">
                                    GPU
                                </Typography>
                                <Switch
                                    checked={useGpu}
                                    onChange={(event) => onUseGpuChange(event.target.checked)}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                            color: "primary.main2",
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                            backgroundColor: "primary.main2",
                                        },
                                    }}
                                />
                            </Stack>
                        </Grid>
                    )}

                    <Grid item xs={12} md={workflowType === "if" ? 2.5 : 2.2} xl={workflowType === "if" ? 2.3 : 2.4}>
                        <Box
                            sx={{
                                display: "flex",
                                height: "100%",
                                alignItems: { xs: "stretch", md: "flex-start" },
                                justifyContent: { xs: "stretch", md: "flex-end" },
                                pt: { xs: 0, md: "33px" },
                            }}
                        >
                            <ButtonWithIcon
                                size="md"
                                icon={<PlayIcon />}
                                onClick={onSubmit}
                                disabled={loading}
                                sx={{ width: { xs: "100%", md: "auto" } }}
                            >
                                INICIAR
                            </ButtonWithIcon>
                        </Box>
                    </Grid>
                </Grid>
            </Stack>
        </Paper>
    )
}

WorkflowForm.propTypes = {
    workflowType: PropTypes.string.isRequired,
    dataInicial: PropTypes.string.isRequired,
    dataFinal: PropTypes.string.isRequired,
    useGpu: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    resetKey: PropTypes.number.isRequired,
    onWorkflowChange: PropTypes.func.isRequired,
    onDataInicialChange: PropTypes.func.isRequired,
    onDataFinalChange: PropTypes.func.isRequired,
    onVerticesFileChange: PropTypes.func.isRequired,
    onResumoFileChange: PropTypes.func.isRequired,
    onUseGpuChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
}

export default WorkflowForm
